import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { courseAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Play, FileText, Loader2, CheckCircle2, BookOpen, ChevronRight, Lock, Mail, Hash } from 'lucide-react';

export default function CoursePortalPage() {
  const { courseId } = useParams();
  const [accessToken, setAccessToken] = useState(null);
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [activeModule, setActiveModule] = useState(0);
  const [activeLesson, setActiveLesson] = useState(0);
  const [userEmail, setUserEmail] = useState('');

  // Check localStorage for saved access
  useEffect(() => {
    const saved = localStorage.getItem(`course_access_${courseId}`);
    if (saved) {
      try {
        const { token } = JSON.parse(saved);
        if (token) { setLoading(true); loadCourse(token); }
      } catch { /* no saved access */ }
    }
  }, [courseId]);

  const loadCourse = async (token) => {
    try {
      const r = await courseAPI.getByToken(token);
      setCourse(r.data.course);
      setProgress(r.data.progress || []);
      setAccessToken(token);
      setUserEmail(r.data.email || '');
    } catch {
      localStorage.removeItem(`course_access_${courseId}`);
      setAccessToken(null);
    } finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!email.trim() || !orderId.trim()) { toast.error('Please enter your email and Order ID'); return; }
    setVerifying(true);
    try {
      const r = await courseAPI.verifyAccess({ email: email.trim(), order_id: orderId.trim() });
      setCourse(r.data.course);
      setProgress(r.data.progress || []);
      setAccessToken(r.data.access_token);
      setUserEmail(email.trim());
      localStorage.setItem(`course_access_${courseId}`, JSON.stringify({ token: r.data.access_token }));
      toast.success('Welcome! Enjoy your course.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid email or Order ID');
    } finally { setVerifying(false); }
  };

  const markComplete = async (lessonId) => {
    if (!accessToken) return;
    const isCompleted = progress.includes(lessonId);
    try {
      if (!isCompleted) {
        await courseAPI.updateProgress(accessToken, { lesson_id: lessonId });
        setProgress(prev => [...prev, lessonId]);
      }
    } catch { /* silent */ }
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
    return url;
  };

  // Loading state
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#195259' }} />
    </div>
  );

  // Login form (no access yet)
  if (!course) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F9FA' }}>
      <div className="w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#195259' }}>
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#195259' }}>Access Your Course</h1>
          <p className="text-sm text-gray-500 mt-2">Enter the email and Order ID from your purchase confirmation email.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" /> Email Address</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your purchase email" className="h-11" data-testid="course-login-email"
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-2"><Hash className="h-4 w-4 text-gray-400" /> Order ID</Label>
            <Input value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="e.g. FNL-XXXX-XXXX" className="h-11 font-mono" data-testid="course-login-order"
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          </div>
          <Button onClick={handleLogin} disabled={verifying} className="w-full h-11 text-white font-semibold" style={{ backgroundColor: '#195259' }} data-testid="course-login-btn">
            {verifying ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying...</> : <><Lock className="h-4 w-4 mr-2" /> Access Course</>}
          </Button>
          <p className="text-[11px] text-gray-400 text-center">Check your purchase confirmation email for the Order ID.</p>
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-xs text-gray-400 hover:text-gray-600">Powered by Funnely</a>
        </div>
      </div>
    </div>
  );

  // Course Player
  const modules = course.modules || [];
  const currentMod = modules[activeModule];
  const currentLesson = currentMod?.lessons?.[activeLesson];
  const totalLessons = modules.reduce((s, m) => s + m.lessons.length, 0);
  const progressPct = totalLessons > 0 ? Math.round((progress.length / totalLessons) * 100) : 0;

  return (
    <div className="flex min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar */}
      <div className="w-80 shrink-0 border-r bg-white flex flex-col" style={{ maxHeight: '100vh', position: 'sticky', top: 0 }}>
        <div className="p-5 border-b" style={{ backgroundColor: '#195259' }}>
          <h1 className="text-white font-semibold text-sm leading-tight">{course.title}</h1>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1 h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: '#FF9A00' }} />
            </div>
            <span className="text-white/70 text-[10px]">{progressPct}%</span>
          </div>
          <p className="text-white/50 text-[10px] mt-1">{progress.length}/{totalLessons} lessons completed</p>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {modules.map((mod, mi) => (
            <div key={mod.id}>
              <button onClick={() => { setActiveModule(mi); if (mod.lessons.length) setActiveLesson(0); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${activeModule === mi ? 'bg-[#195259]/10 text-[#195259]' : 'text-gray-600 hover:bg-gray-50'}`}>
                <span className="text-[10px] text-gray-400 mr-1.5">{mi + 1}.</span> {mod.title}
              </button>
              {mod.lessons.map((les, li) => {
                const isActive = activeModule === mi && activeLesson === li;
                const isDone = progress.includes(les.id);
                return (
                  <button key={les.id} onClick={() => { setActiveModule(mi); setActiveLesson(li); }}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 ml-3 rounded-lg text-xs transition-colors ${isActive ? 'bg-[#FF9A00]/10 text-[#195259] font-medium' : 'text-gray-500 hover:bg-gray-50'}`}>
                    {isDone ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : les.video_url ? <Play className="h-3.5 w-3.5 shrink-0" /> : <FileText className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{les.title}</span>
                    {les.duration_mins > 0 && <span className="text-[9px] text-gray-400 shrink-0 ml-auto">{les.duration_mins}m</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="p-3 border-t text-[10px] text-gray-400 text-center">
          Logged in as {userEmail}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-[#F8F9FA]">
        {currentLesson ? (
          <div>
            {currentLesson.video_url && (
              <div className="bg-black">
                <div className="max-w-4xl mx-auto aspect-video">
                  <iframe src={getEmbedUrl(currentLesson.video_url)} className="w-full h-full" allowFullScreen title={currentLesson.title} style={{ border: 'none' }} />
                </div>
              </div>
            )}

            <div className="max-w-3xl mx-auto px-8 py-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{currentMod?.title} &middot; Lesson {activeLesson + 1}</p>
                  <h2 className="text-xl font-semibold mt-1" style={{ color: '#195259' }}>{currentLesson.title}</h2>
                </div>
                <button onClick={() => markComplete(currentLesson.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${progress.includes(currentLesson.id) ? 'bg-emerald-100 text-emerald-700' : 'bg-white border text-gray-600 hover:border-[#195259]'}`}
                  data-testid="mark-complete-btn">
                  <CheckCircle2 className="h-4 w-4" />
                  {progress.includes(currentLesson.id) ? 'Completed' : 'Mark as Complete'}
                </button>
              </div>

              {currentLesson.content && (
                <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap leading-relaxed">{currentLesson.content}</div>
              )}

              {currentLesson.file_url && (
                <a href={currentLesson.file_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 rounded-xl border bg-white text-sm font-medium text-gray-700 hover:border-[#195259] transition-colors">
                  <FileText className="h-4 w-4" /> {currentLesson.file_name || 'Download Attachment'}
                </a>
              )}

              {(activeLesson < (currentMod?.lessons?.length || 0) - 1 || activeModule < modules.length - 1) && (
                <button onClick={() => {
                  if (activeLesson < (currentMod?.lessons?.length || 0) - 1) { setActiveLesson(activeLesson + 1); }
                  else if (activeModule < modules.length - 1) { setActiveModule(activeModule + 1); setActiveLesson(0); }
                }} className="mt-8 flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90" style={{ backgroundColor: '#195259' }} data-testid="next-lesson-btn">
                  Next Lesson <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <BookOpen className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">Select a lesson to begin</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
